/**
 * MetaBinding — title/subtitle/tag_list/category 양방향 동기화
 *
 * Y.Map('meta')에 메타데이터를 저장하고, input 요소와 양방향 바인딩.
 * 한쪽에서 수정하면 다른 쪽에도 실시간 반영.
 *
 * 각 필드의 현재 편집자를 awareness로 표시 (색상 테두리 + 이름 label).
 */

export class MetaBinding {
  /**
   * @param {Object} options
   * @param {import('yjs').Map} options.yMeta - Y.Map('meta')
   * @param {import('yjs').Doc} options.doc
   * @param {import('y-protocols/awareness').Awareness} options.awareness
   * @param {import('./cable_provider').CableProvider} options.provider
   * @param {Object} options.fields - { title: HTMLInputElement, subtitle: HTMLInputElement, tagList: HTMLInputElement, postCategorySlug: HTMLSelectElement }
   * @param {Function} [options.onBlur] - 필드에서 blur 시 호출 (autosave 트리거용)
   */
  constructor({ yMeta, doc, awareness, provider, fields, onBlur }) {
    this.yMeta = yMeta
    this.doc = doc
    this.awareness = awareness
    this.provider = provider
    this.fields = fields
    this.onBlur = onBlur
    this.destroyed = false
    this._isApplyingRemote = false

    this._inputHandlers = new Map()
    this._focusHandlers = new Map()
    this._indicators = new Map()

    this._initFields()
    this._setupYjsObserver()
    this._setupAwarenessObserver()
  }

  destroy() {
    this.destroyed = true

    for (const [fieldName, handler] of this._inputHandlers) {
      const el = this.fields[fieldName]
      if (el) {
        el.removeEventListener('input', handler)
        el.removeEventListener('change', handler)
      }
    }
    for (const [fieldName, handlers] of this._focusHandlers) {
      const el = this.fields[fieldName]
      if (el) {
        el.removeEventListener('focus', handlers.focus)
        el.removeEventListener('blur', handlers.blur)
      }
    }
    for (const indicator of this._indicators.values()) {
      indicator.remove()
    }

    this.yMeta.unobserve(this._yMetaObserver)
    this.awareness.off('change', this._awarenessHandler)
  }

  // --- 초기화 ---

  _initFields() {
    for (const [fieldName, el] of Object.entries(this.fields)) {
      if (!el) continue

      // 1. 현재 input 값을 Y.Map에 저장 (최초 로드)
      const currentValue = el.value || ''
      const yValue = this.yMeta.get(fieldName)
      if (yValue === undefined) {
        this.yMeta.set(fieldName, currentValue)
      } else {
        // Y.Map에 이미 값이 있으면 input에 반영
        el.value = yValue
      }

      // 2. input → Y.Map (로컬 변경)
      const handler = () => {
        if (this._isApplyingRemote || this.destroyed) return
        this.yMeta.set(fieldName, el.value)
      }

      if (el.tagName === 'SELECT') {
        el.addEventListener('change', handler)
      } else {
        el.addEventListener('input', handler)
      }
      this._inputHandlers.set(fieldName, handler)

      // 3. focus/blur → awareness (현재 편집 중인 필드 표시)
      const focusHandler = () => {
        this.awareness.setLocalStateField('metaField', fieldName)
      }
      const blurHandler = () => {
        this.awareness.setLocalStateField('metaField', null)
        this.onBlur?.()
      }
      el.addEventListener('focus', focusHandler)
      el.addEventListener('blur', blurHandler)
      this._focusHandlers.set(fieldName, { focus: focusHandler, blur: blurHandler })
    }
  }

  // --- Y.Map → input (원격 변경) ---

  _setupYjsObserver() {
    this._yMetaObserver = (event, transaction) => {
      // 로컬 변경이면 무시
      if (transaction.origin !== this.provider) return
      if (this.destroyed) return

      this._isApplyingRemote = true
      try {
        for (const [key, change] of event.keys) {
          const el = this.fields[key]
          if (!el) continue

          const newValue = this.yMeta.get(key) ?? ''
          if (el.value !== newValue) {
            el.value = newValue
          }
        }
      } finally {
        this._isApplyingRemote = false
      }
    }
    this.yMeta.observe(this._yMetaObserver)
  }

  // --- Awareness → 편집 중 표시 ---

  _setupAwarenessObserver() {
    this._awarenessHandler = () => {
      if (this.destroyed) return

      // 다른 사용자가 어떤 meta 필드를 편집 중인지 표시
      const localClientId = this.awareness.clientID
      const activeFields = new Map() // fieldName → { name, color }

      this.awareness.getStates().forEach((state, clientId) => {
        if (clientId === localClientId) return
        if (!state?.metaField || !state?.user) return

        activeFields.set(state.metaField, {
          name: state.user.name,
          color: state.user.color,
        })
      })

      // 각 필드에 indicator 표시/숨기기
      for (const [fieldName, el] of Object.entries(this.fields)) {
        if (!el) continue

        const editor = activeFields.get(fieldName)
        if (editor) {
          this._showIndicator(fieldName, el, editor)
        } else {
          this._hideIndicator(fieldName)
        }
      }
    }
    this.awareness.on('change', this._awarenessHandler)
  }

  _showIndicator(fieldName, el, { name, color }) {
    let indicator = this._indicators.get(fieldName)

    // 필드에 색상 하단 보더 적용 (inline style)
    el._collabOriginalBorder = el._collabOriginalBorder ?? el.style.borderBottom
    el.style.borderBottom = `2px solid ${color}`

    if (!indicator) {
      // 이름 태그 — 필드 우측 상단에 작은 label (input은 void element이므로 부모에 삽입)
      indicator = document.createElement('div')
      indicator.className = 'collab-meta-label'
      indicator.style.cssText = `
        position: absolute;
        top: 0; right: 0;
        font-size: 10px;
        line-height: 14px;
        padding: 0 5px;
        color: white;
        border-radius: 3px;
        white-space: nowrap;
        pointer-events: none;
        z-index: 10;
      `
      const parent = el.parentElement
      if (parent) {
        parent._collabOriginalPosition = parent._collabOriginalPosition ?? parent.style.position
        parent.style.position = 'relative'
        parent.appendChild(indicator)
      }
      this._indicators.set(fieldName, indicator)
    }

    indicator.style.background = color
    indicator.textContent = name
    indicator.style.display = 'block'
  }

  _hideIndicator(fieldName) {
    const el = this.fields[fieldName]
    const indicator = this._indicators.get(fieldName)

    if (el) {
      el.style.borderBottom = el._collabOriginalBorder ?? ''
    }
    if (indicator) {
      indicator.style.display = 'none'
    }
  }
}
